"use client";

import React from "react";

import { WorkflowAction } from "@/configuration/document-mappings/types";
import { CopyableContent } from "@/src/components/copyable-content";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/src/components/shadcn/ui/card";
import { ApprovalEvidenceCard } from "@/src/components/tasks/approval-evidence-card";
import { OrderMatchPanelView } from "@/src/components/tasks/order-match-panel";
import { GlobalAlertInstructions, TeDecisionFlow } from "@/src/components/tasks/pa-east-workflow-action-content";
import { ReferralMatchPanelView } from "@/src/components/tasks/referral-match-panel";
import {
  InstructionField,
  ProcessedInstructions,
  useProcessedInstructions,
} from "@/src/components/tasks/use-processed-instructions";
import { cn } from "@/src/lib/shadcn-utils";
import { Section } from "@/src/models/section";
import { Task } from "@/src/models/task";
import { type TaskApproval } from "@/src/models/task-approval";

// A leading tab on an `instructions` config string marks it as a sub-item of the preceding line.
const NESTED_INSTRUCTION_MARKER = "\t";

const COPYABLE_PREFIXES = [
  "Document label:",
  "Upload to folder:",
  "Assign to:",
  "Description:",
  "Document type:",
  "Workflow Type:",
  "MOCK CHART:",
  "Reason:",
  "Assigned:",
  "Upload the Document to Patient and assign to ",
  "AHI: ",
  "Summary Statement:",
  "Recommendation:",
  "Doc Label:",
  "Document Type:",
  "ECW Folder :",
  "Scanned By:",
  "Transfer Reason:",
  "Signature Page:",
  "Incomplete Reason:",
  "Task Type:",
  "Task Details:",
  "Patient Name:",
  "DOB:",
  "Document Code:",
  "User:",
  "Body Part:",
];

function InstructionFieldRow({ label, value }: InstructionField) {
  return (
    <p className="whitespace-pre-line">
      <strong>{label}:</strong> <CopyableContent text={value} />
    </p>
  );
}

function processCopyableInstruction(
  instruction: string,
  index: number,
  processInstructionText: (text: string) => string
) {
  const isNested = instruction.startsWith(NESTED_INSTRUCTION_MARKER);
  const nestedClassName = isNested ? "ml-6" : undefined;
  const renderedInstruction = processInstructionText(
    isNested ? instruction.slice(NESTED_INSTRUCTION_MARKER.length) : instruction
  );

  const matchedPrefix = COPYABLE_PREFIXES.find((prefix) => renderedInstruction.startsWith(prefix));

  if (matchedPrefix) {
    let labelValue = renderedInstruction.substring(matchedPrefix.length).trim();

    // Special handling for MOCK CHART to exclude account number
    if (matchedPrefix === "MOCK CHART:") {
      labelValue = labelValue.replace(/\s*\(Acct\s*#\d+\)\s*$/, "");
    }

    return (
      <li key={index} className={cn(nestedClassName, matchedPrefix === "Description:" && "whitespace-pre-wrap")}>
        {matchedPrefix} <CopyableContent text={labelValue} />
      </li>
    );
  }

  return (
    <li key={index} className={nestedClassName}>
      {renderedInstruction}
    </li>
  );
}

function InstructionExtraFieldRow({ label, value }: InstructionField) {
  return (
    <li>
      {label}: <CopyableContent text={value} />
    </li>
  );
}

function renderCustomInstructionList(
  instructions: readonly string[],
  extraFields: InstructionField[],
  processInstructionText: (text: string) => string
) {
  const staffLocationField = extraFields.find((field) => field.label === "Staff Location");
  const otherExtraFields = extraFields.filter((field) => field.label !== "Staff Location");
  let staffLocationInserted = false;

  const instructionItems = instructions
    .filter((instruction) => instruction.trim() !== "")
    .flatMap((instruction, index) => {
      const renderedInstruction = processInstructionText(instruction);
      const insertStaffLocationAfter =
        renderedInstruction.startsWith("Assign to:") && staffLocationField != null && !staffLocationInserted;

      if (insertStaffLocationAfter) {
        staffLocationInserted = true;
      }

      const items = [processCopyableInstruction(instruction, index, processInstructionText)];

      if (insertStaffLocationAfter) {
        items.push(
          <InstructionExtraFieldRow
            key="staff-location"
            label={staffLocationField.label}
            value={staffLocationField.value}
          />
        );
      }

      return items;
    });

  return [
    ...instructionItems,
    ...otherExtraFields.map((field) => <InstructionExtraFieldRow key={field.label} {...field} />),
  ];
}

export function InstructionsStateCard({ message }: { message: string }) {
  return (
    <Card className="bg-green-500/5 border border-green-500/20">
      <CardHeader>
        <CardTitle className="text-center">Document Management Instructions</CardTitle>
      </CardHeader>
      <CardContent>
        <p>{message}</p>
      </CardContent>
    </Card>
  );
}

export function DocumentInstructionsCardView({ data }: { data: ProcessedInstructions }) {
  return (
    <Card className="bg-green-500/5 border border-green-500/20">
      <CardHeader>
        <CardTitle className="text-center">Document Management Instructions</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-2 text-sm">
          <p>
            <strong>Document Management Instructions - {data.displayName}</strong>
          </p>

          {/* Standard fields */}
          {!data.instructions && (
            <div className="mb-2">
              <p>
                <strong>Instructions:</strong>
              </p>
              <div className="pl-4">
                {data.standardFields.map((field) => (
                  <InstructionFieldRow key={field.label} {...field} />
                ))}
                {data.extraFields.map((field) => (
                  <React.Fragment key={field.label}>
                    <InstructionFieldRow {...field} />
                    {field.label === "Fax sent to" &&
                      data.additionalWorkflow != null &&
                      data.additionalWorkflow.length > 0 &&
                      data.additionalWorkflow.map((instruction, index) => (
                        <p key={index} className="whitespace-pre-line">
                          <strong>Additional Workflow:</strong>{" "}
                          <CopyableContent text={data.processInstructionText(instruction)} />
                        </p>
                      ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Custom instructions */}
          {data.instructions && (
            <>
              <p>
                <strong>Instructions:</strong>
              </p>
              {data.statusLine && (
                <p>
                  <strong>{data.processInstructionText(data.statusLine)}</strong>
                </p>
              )}
              <ul className="list-disc pl-6 space-y-1">
                {renderCustomInstructionList(data.instructions, data.extraFields, data.processInstructionText)}
              </ul>
            </>
          )}

          {data.note && (
            <>
              <p>
                <strong>{data.noteLabel ?? "Note"}:</strong>
              </p>
              <p className="pl-4 whitespace-pre-line">{data.processInstructionText(data.note)}</p>
            </>
          )}

          {/* TE Decision Flow */}
          {data.workflowAction === WorkflowAction.TE && <TeDecisionFlow />}

          {/* Additional workflow */}
          {data.additionalWorkflow &&
            Array.isArray(data.additionalWorkflow) &&
            !data.extraFields.some((f) => f.label === "Fax sent to") && (
              <div className="mb-2">
                <p>
                  <strong>Additional Workflow:</strong>
                </p>
                <div className="pl-4">
                  {data.additionalWorkflow.map((instruction, index) => {
                    const rendered = data.processInstructionText(instruction);
                    const hasGlobalAlert = /global alert/i.test(rendered);

                    return (
                      <React.Fragment key={index}>
                        {processCopyableInstruction(instruction, index, data.processInstructionText)}
                        {hasGlobalAlert && <GlobalAlertInstructions />}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

          {data.tags && data.tags.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <strong>Tags:</strong>
              {data.tags.map((tag) => (
                <Badge key={tag} variant="secondary">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {data.ccDescription &&
            !data.instructions?.some((instruction) =>
              data.processInstructionText(instruction).startsWith("Description:")
            ) && (
              <div className="mt-2">
                <p>
                  <strong>Description (will be filed with document):</strong>
                </p>
                <pre className="pl-4 whitespace-pre-wrap font-sans">
                  <CopyableContent text={data.ccDescription} />
                </pre>
              </div>
            )}

          <OrderMatchPanelView match={data.stepOutputs?.order_match} />
          <ReferralMatchPanelView match={data.stepOutputs?.referral_match} />
        </div>
      </CardContent>
    </Card>
  );
}

export function DocumentManagementInstructions({
  task,
  section,
  pendingApproval,
}: {
  task: Task;
  section: Section;
  pendingApproval?: TaskApproval | null;
}) {
  const { data, isLoading, isError } = useProcessedInstructions(task, section, !pendingApproval);

  if (pendingApproval) {
    return <ApprovalEvidenceCard approval={pendingApproval} />;
  }

  if (isLoading) {
    return <InstructionsStateCard message="Loading instructions..." />;
  }

  if (isError || !data) {
    return <InstructionsStateCard message="Error loading instructions or no instructions found." />;
  }

  return <DocumentInstructionsCardView data={data} />;
}
